import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ESC, focusables, useEscape } from '@/lib/keys';
import { usePresence } from '@/lib/hooks';
import { Icon } from './Icon';
import { Kbds } from './bits';
import type { IconName } from './icons';

// Floating menus and small panels anchored to a button, placed below it (or above when there is no room).

export interface PopoverState {
  anchor: HTMLElement | null;
  open: boolean;
  close: () => void;
  /** Opens at the clicked element, or closes when it is already open there. */
  toggle: (e: MouseEvent<HTMLElement>) => void;
  openAt: (el: HTMLElement) => void;
  /** Spread onto the anchor button. */
  anchorProps: { 'aria-expanded': boolean; 'aria-haspopup': 'menu' | 'dialog'; onClick: (e: MouseEvent<HTMLElement>) => void };
}

export function usePopover(kind: 'menu' | 'dialog' = 'menu'): PopoverState {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = useCallback(() => setAnchor(null), []);
  const toggle = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const el = e.currentTarget;
    setAnchor((a) => (a === el ? null : el));
  }, []);
  const openAt = useCallback((el: HTMLElement) => setAnchor(el), []);
  return {
    anchor,
    open: anchor !== null,
    close,
    toggle,
    openAt,
    anchorProps: { 'aria-expanded': anchor !== null, 'aria-haspopup': kind, onClick: toggle },
  };
}

interface PopoverContextValue {
  close: () => void;
  /** Registers a popover opened from inside this one, so clicks and scrolls in it count as inside. */
  nest: (contains: (node: Node) => boolean) => () => void;
}

const PopoverContext = createContext<PopoverContextValue | null>(null);

/** Closes the popover this component sits in. */
export function usePopoverClose(): () => void {
  return useContext(PopoverContext)?.close ?? (() => undefined);
}

interface PopoverProps {
  state: PopoverState;
  children: ReactNode;
  /** Minimum width in pixels. */
  width?: number;
  maxWidth?: number;
  /** Which edge lines up with the anchor. */
  align?: 'right' | 'left';
  /** Moves focus to the first item when it opens. */
  focus?: boolean;
  className?: string;
  role?: 'menu' | 'dialog' | 'listbox';
  label?: string;
  style?: CSSProperties;
  id?: string;
  /** Selector for the element that takes focus when it opens, in place of the first item. */
  initialFocus?: string;
}

export function Popover({
  state,
  children,
  width = 220,
  maxWidth = 340,
  align = 'right',
  focus = true,
  className,
  role = 'menu',
  label,
  style,
  id,
  initialFocus,
}: PopoverProps) {
  const { open, anchor, close } = state;
  const { mounted, shown } = usePresence(open, 200);
  const ref = useRef<HTMLDivElement>(null);
  const lastAnchor = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number; origin: string } | null>(null);
  if (anchor) lastAnchor.current = anchor;

  // A date picker or time list opened from inside this popover lives in its own portal; clicks there are still "inside".
  const parent = useContext(PopoverContext);
  const nested = useRef(new Set<(node: Node) => boolean>());
  const contains = useCallback((node: Node) => !!ref.current?.contains(node) || [...nested.current].some((inside) => inside(node)), []);
  const nest = useCallback((inside: (node: Node) => boolean) => {
    nested.current.add(inside);
    return () => {
      nested.current.delete(inside);
    };
  }, []);
  useEffect(() => {
    if (!mounted || !parent) return;
    return parent.nest(contains);
  }, [mounted, parent, contains]);

  const place = useCallback(() => {
    const el = ref.current;
    const a = lastAnchor.current;
    if (!el || !a) return;
    const r = a.getBoundingClientRect();
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;
    let x = align === 'left' ? r.left : r.right - pw;
    let y = r.bottom + 6;
    let up = false;
    if (y + ph > window.innerHeight - 8 && r.top - ph - 6 > 8) {
      y = r.top - ph - 6;
      up = true;
    }
    x = Math.max(8, Math.min(window.innerWidth - pw - 8, x));
    y = Math.max(8, y);
    setPos({ x, y, origin: `${up ? 'bottom' : 'top'} ${align}` });
  }, [align]);

  useLayoutEffect(() => {
    if (mounted && open) place();
  }, [mounted, open, place, children]);

  const visible = shown && open && pos !== null;

  // Focus the first item once it is placed and visible (a hidden popover cannot take focus).
  useEffect(() => {
    if (!visible || !focus) return;
    const first =
      (initialFocus ? ref.current?.querySelector<HTMLElement>(initialFocus) : null) ??
      ref.current?.querySelector<HTMLElement>('.mi:not([disabled]), input, textarea, button:not([disabled])');
    first?.focus({ preventScroll: true });
  }, [visible, focus, initialFocus]);

  const closeAndReturn = useCallback(() => {
    const a = lastAnchor.current;
    const active = document.activeElement;
    close();
    if (a && a.isConnected && (!active || active === document.body || contains(active))) a.focus({ preventScroll: true });
  }, [close, contains]);

  useEscape(open, ESC.popover, () => {
    closeAndReturn();
  });

  useEffect(() => {
    if (!open) return;
    const down = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (contains(t)) return;
      if (lastAnchor.current?.contains(t)) return;
      close();
    };
    const scroll = (e: Event) => {
      if (e.target instanceof Node && contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', down);
    window.addEventListener('resize', close);
    document.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('mousedown', down);
      window.removeEventListener('resize', close);
      document.removeEventListener('scroll', scroll, true);
    };
  }, [open, close, contains]);

  const ctx = useMemo(() => ({ close: closeAndReturn, nest }), [closeAndReturn, nest]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      // A form in a popover keeps Tab to itself. The popover sits in a portal, so the browser's next stop would be
      // outside it, and an open dialog or composer would pull focus back to its own first field.
      const form = e.target instanceof Element ? e.target.closest('form') : null;
      if (e.defaultPrevented || !form || !ref.current?.contains(form)) return;
      const stops = focusables(form);
      if (!stops.length) return;
      e.preventDefault();
      const i = stops.indexOf(document.activeElement as HTMLElement);
      const to = e.shiftKey ? (i <= 0 ? stops.length - 1 : i - 1) : i < 0 || i === stops.length - 1 ? 0 : i + 1;
      stops[to]!.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('.mi:not([disabled])') ?? [])];
    if (!items.length) return;
    // Arrow keys inside a text box move the caret, except to step from the box into the list.
    const inInput = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
    if (inInput && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    let next = 0;
    if (e.key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % items.length;
    else if (e.key === 'ArrowUp') next = i <= 0 ? items.length - 1 : i - 1;
    else if (e.key === 'End') next = items.length - 1;
    items[next]!.focus();
  };

  if (!mounted) return null;
  return createPortal(
    <PopoverContext.Provider value={ctx}>
      <div
        ref={ref}
        id={id}
        className={['pop', visible ? 'show' : '', className].filter(Boolean).join(' ')}
        role={role}
        aria-label={label}
        style={{
          minWidth: width,
          maxWidth,
          left: pos?.x ?? 0,
          top: pos?.y ?? 0,
          transformOrigin: pos?.origin,
          ...style,
        }}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </PopoverContext.Provider>,
    document.body,
  );
}

interface MenuItemProps {
  icon?: IconName;
  /** An avatar or monogram in place of the icon. */
  lead?: ReactNode;
  children: ReactNode;
  sub?: ReactNode;
  keys?: string;
  /** Shows a tick at the end (the current choice). */
  checked?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** Keeps the popover open after the click. */
  keep?: boolean;
  onSelect?: () => void;
  className?: string;
}

export function MenuItem({ icon, lead, children, sub, keys, checked, danger, disabled, keep, onSelect, className }: MenuItemProps) {
  const close = usePopoverClose();
  return (
    <button
      type="button"
      className={['mi', danger ? 'danger' : '', className].filter(Boolean).join(' ')}
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked === undefined ? undefined : checked}
      disabled={disabled}
      onClick={() => {
        if (!keep) close();
        onSelect?.();
      }}
    >
      {lead ?? <Icon name={icon ?? 'chev-right'} />}
      <span className="grow">{children}</span>
      {sub ? <span className="sub">{sub}</span> : null}
      {keys ? (
        <span className="mi-keys">
          <Kbds keys={keys} />
        </span>
      ) : null}
      {checked ? <Icon name="check" className="mi-on" /> : null}
    </button>
  );
}

export function MenuSep() {
  return <div className="msep" role="separator" />;
}

export function MenuHead({ children }: { children: ReactNode }) {
  return (
    <div className="mhead" role="presentation">
      {children}
    </div>
  );
}
